import { useId, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/cn'
import styles from './Field.module.css'

export interface FieldProps {
  label: string
  /** Rendered by `children(ids)` so the control gets the right aria wiring. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
  hint?: string
  error?: string
  required?: boolean
  className?: string
}

/**
 * Label, hint and error for one control.
 *
 * The control receives its id and `aria-describedby` through the render prop, so
 * a validation message is announced rather than only being visible — the error
 * and the input can never drift apart.
 */
export function Field({ label, children, hint, error, required = false, className }: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn(styles.field, className)}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        ) : null}
        {required ? <span className="visually-hidden"> (required)</span> : null}
      </label>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {hint && !error ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}

      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          <AlertCircle aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  )
}
